<template>
  <div>
    <v-btn v-if="icon" @click="selectFile" color="accent" icon><v-icon>mdi-upload</v-icon></v-btn>
    <v-btn v-if="!icon && !galleryOnly" @click="selectFile" class="inset upload-btn" outlined color="primary">
      <v-icon>mdi-plus</v-icon>
      {{ label ? label : $t('upload_image')}}
    </v-btn>
    <v-btn
      v-if="showSelectDialog"
      class="gallery-btn mt-3 w-100 mb-4 mb-md-0"
      @click="openSelectImageDialog"
      color="primary"
      outlined>
      {{ $t('select_from_gallery') }}
    </v-btn>
    <input
      :ref="`input-${inputKey}`"
      :id="inputKey"
      @click="resetFileSelector"
      @change="setImage"
      type="file"
      name="image"
      accept="image/png, image/jpeg, image/jpg, image/gif, image/tiff, image/tif, image/svg+xml">
    <v-dialog scrollable width="700" v-model="openModal">
      <v-card>
        <v-card-title class="headline">{{$t('image_upload')}}</v-card-title>
        <v-card-text>
          <vue-cropper
            :ref="`cropper-${inputKey}`"
            :src="this.src"
            :viewMode="1"
            :containerStyle="containerStyle"
            :zoomable="false"
            :aspect-ratio="ratio ? ratio : NaN"
          >
          </vue-cropper>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="close">{{$t('close')}}</v-btn>
          <v-btn text @click="save" color="primary">{{$t('save')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <SelectImageDialog
      :display-select-image-dialog="displaySelectImageDialog"
      @update:displaySelectImageDialog="closeSelectImageDialog"
      @gallery-item-selected="handleGalleryItemSelected"
      :isAllowedEdit="isAllowed('gallery_edit')"
      :appTags="['stc-editor-image','st-portal-prod-image']"
    />
  </div>
</template>

<script>
import { SelectImageDialog } from '@scantrust/st-gallery'
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { mapGetters } from 'vuex'

export default {
  name: 'ImageCropper',
  props: ['src', 'icon', 'input-key', 'ratio', 'label', 'showSelectDialog', 'galleryOnly'],
  components: {
    VueCropper,
    SelectImageDialog
  },
  data () {
    return {
      openModal: false,
      type: null,
      croppedImg: null,
      containerStyle: {
        width: '100%',
        'max-height': '450px'
      },
      displaySelectImageDialog: false,
      isFromGallery: false
    }
  },
  computed: {
    ...mapGetters(['isAllowed'])
  },
  methods: {
    cropImage () {
      if (this.$refs[`cropper-${this.inputKey}`]) {
        this.croppedImg = this.$refs[`cropper-${this.inputKey}`].getCroppedCanvas().toDataURL(this.type)
      }
    },

    resetFileSelector () {
      this.$refs[`input-${this.inputKey}`].value = ''
    },

    async save () {
      this.cropImage()
      this.isFromGallery = false
      this.$emit('image-selected', this.croppedImg, this.isFromGallery)
      this.close()
    },

    close () {
      this.$refs[`cropper-${this.inputKey}`].value = ''
      this.croppedImg = false
      this.openModal = false
    },

    setImage (e) {
      const file = e.target.files[0]
      if (!file) {
        return
      }
      if (file.type.indexOf('image/') === -1) {
        alert('Please select an image file')
        return
      }
      this.type = file.type
      if (typeof FileReader === 'function') {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.imgSrc = event.target.result

          if (this.type !== 'image/svg+xml') {
            this.openModal = true
            setTimeout(() => {
              this.$refs[`cropper-${this.inputKey}`].replace(event.target.result)
            }, 200)
          } else {
            this.openModal = false
            this.$emit('image-selected', this.imgSrc)
          }
        }
        reader.readAsDataURL(file)
      } else {
        alert('Sorry, FileReader API not supported')
      }
    },

    selectFile () {
      const input = document.getElementById(this.inputKey)

      input.click()
    },

    openSelectImageDialog () {
      this.displaySelectImageDialog = true
    },

    closeSelectImageDialog () {
      this.displaySelectImageDialog = false
    },

    handleGalleryItemSelected (item) {
      this.isFromGallery = true
      this.$emit('image-selected', item, this.isFromGallery)
    }
  }
}
</script>

<style lang="sass" scoped>

input
  display: none

.upload-btn
  width: 100%

// The label is translated and several languages are much longer than the English
// original, while the button is capped by the width of the product image column.
// A smaller, less stretched label keeps every current language on one line, and
// the button grows to two lines rather than clipping if a longer one appears.
// !important overrides Vuetify's own .v-btn size rules, which are more specific.
.gallery-btn
  height: auto !important
  min-height: 36px
  padding: 8px 10px !important
  white-space: normal

.gallery-btn ::v-deep .v-btn__content
  flex: 1 1 auto
  max-width: 100%
  white-space: normal
  text-transform: none
  font-size: 13px
  font-weight: 600
  letter-spacing: 0
  line-height: 1.35

</style>
